import * as React from 'react';
import { useDispatch } from 'react-redux';
import {AutoComplete, AutoCompleteProps} from "primereact/autocomplete";
import { User } from '../../../../settings/users/models/User';
import { constantTable } from "../../../../../helpers/constants";
import { useCancellablePromiseWrapper } from "../../../../../helpers/hooks/useCancellablePromiseWrapper";
import { alertInfo } from '../../../../../store/reducers/Alert';
import { services } from '../../../../../services';

interface AutoCompleteCompleteMethodParams {
	originalEvent: React.SyntheticEvent;
	query: string;
}

interface UserSearchAutoCompleteProps extends AutoCompleteProps {
	searchUser?: User;
	field: string;
	disabled?: boolean;
	onChange: (u: any) => void;
}

export const UserSearchAutoComplete: React.FC<UserSearchAutoCompleteProps> = (props) => {
	const { searchUser: searchUserProp, field, onChange, disabled, ...autoCompleteProps } = props;

	const promiseWrap = useCancellablePromiseWrapper();

	const [searchUser, setSearchUser] = React.useState<User>();
	React.useEffect(() => {
		setSearchUser(searchUserProp);
	}, [searchUserProp]);

	const [userSearchResult, setUserSearchResult] = React.useState<User[]>([]);
	const requestGenerationRef = React.useRef(0);
	const [searchRequest, setSearchRequest] = React.useState({ query: ``, generation: 0 });
	const onUserSearch = (event: any) => {
		const typedEvent: AutoCompleteCompleteMethodParams = event as AutoCompleteCompleteMethodParams;
		setSearchRequest({
			query: typedEvent.query,
			generation: requestGenerationRef.current,
		});
	};
	const typingTimeoutRef = React.useRef(0);
	React.useEffect(() => {
		if (typingTimeoutRef.current !== 0) clearTimeout(typingTimeoutRef.current);

		const normalizedQuery = searchRequest.query.trim();
		const requestGeneration = searchRequest.generation;
		if (normalizedQuery) {
			typingTimeoutRef.current = window.setTimeout(() => {
				promiseWrap(
					(cancelToken) => services.userService.searchNotificationUsers(
						normalizedQuery,
						constantTable.searchRows,
						cancelToken,
					),
					(res: User[]) => {
						if (requestGenerationRef.current === requestGeneration) {
							setUserSearchResult(res);
						}
					}, true
				);
			}, 1500);
		}

		return () => clearTimeout(typingTimeoutRef.current);
		// eslint-disable-next-line
	}, [searchRequest]);

	const itemTemplate = (user: User) => {
		return (
			<div className="user-search">
				<span className="font-weight-500">{user.Title}</span>
				{(user.Email || user.Login) && (
					<small className="text-muted">{user.Email || user.Login}</small>
				)}
			</div>
		);
  }
	const dispatch = useDispatch();
	return <AutoComplete
		className="w-100"
		appendTo={document.body}
		value={searchUser}
		suggestions={userSearchResult}
		completeMethod={onUserSearch}
		itemTemplate={itemTemplate}
		placeholder="Введите логин пользователя.."
		disabled={disabled}
		{...autoCompleteProps}
		onSelect={(e) => {
			onChange(e.value);
		}}
		onUnselect={(e) => {
			onChange(null);
		}}
		onChange={(e) => {
			if (typeof (e.value) === `string`) {
				requestGenerationRef.current += 1;
				setUserSearchResult([]);
				if (!e.value.trim())
					setSearchRequest({ query: ``, generation: requestGenerationRef.current });
			}

			if (e.value != null && typeof (e.value) === `object`) {
				if (field in e.value)
					dispatch(alertInfo({ summary: `Пользователь выбран`, detail: e.value.Title }));
			}
			else if (searchUserProp)
				onChange(null);

			setSearchUser(e.value);
		}}
	/>
}
